/**
 * Color System Showcase for Visual Regression Testing
 *
 * Displays all color tokens from the design system.
 * Used by Playwright to detect color regressions.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface ColorSwatchProps {
  name: string;
  variable: string;
  description?: string;
}

function ColorSwatch({ name, variable, description }: ColorSwatchProps) {
  return (
    <div className="space-y-2">
      <div
        className={`h-24 w-full rounded-lg border ${variable}`}
        style={{ backgroundColor: `hsl(var(${variable.replace(/^bg-/, "--")}))` }}
      />
      <div className="space-y-1">
        <p className="text-sm font-medium">{name}</p>
        <p className="text-xs text-muted-foreground font-mono">{variable}</p>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
    </div>
  );
}

export default function ColorSystem() {
  return (
    <div className="container mx-auto p-8 space-y-12 max-w-6xl">
      <div className="space-y-2">
        <h1 className="text-4xl font-bold">Color System</h1>
        <p className="text-muted-foreground">
          Complete color palette from the design system
        </p>
      </div>

      <Separator />

      {/* Primary Colors */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Primary Colors</h2>
          <p className="text-sm text-muted-foreground">
            Main brand colors used throughout the application
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          <ColorSwatch
            name="Primary"
            variable="--primary"
            description="Main brand color (Teal)"
          />
          <ColorSwatch
            name="Primary Foreground"
            variable="--primary-foreground"
            description="Text on primary background"
          />
          <ColorSwatch
            name="Secondary"
            variable="--secondary"
            description="Secondary brand color"
          />
          <ColorSwatch
            name="Secondary Foreground"
            variable="--secondary-foreground"
            description="Text on secondary background"
          />
        </div>
      </section>

      <Separator />

      {/* Category Colors */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Category Colors</h2>
          <p className="text-sm text-muted-foreground">
            Colors for receipt categorization and data visualization
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          <ColorSwatch
            name="Food"
            variable="--category-food"
            description="Amber - Food & Beverages"
          />
          <ColorSwatch
            name="Household"
            variable="--category-household"
            description="Teal - Household Items"
          />
          <ColorSwatch
            name="Health"
            variable="--category-health"
            description="Green - Health & Wellness"
          />
          <ColorSwatch
            name="Personal"
            variable="--category-personal"
            description="Purple - Personal Care"
          />
          <ColorSwatch
            name="Other"
            variable="--category-other"
            description="Gray - Miscellaneous"
          />
        </div>
      </section>

      <Separator />

      {/* Semantic Colors */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Semantic Colors</h2>
          <p className="text-sm text-muted-foreground">
            Status and feedback colors
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          <ColorSwatch
            name="Destructive"
            variable="--destructive"
            description="Error states and dangerous actions"
          />
          <ColorSwatch
            name="Destructive Foreground"
            variable="--destructive-foreground"
            description="Text on destructive background"
          />
          <ColorSwatch
            name="Muted"
            variable="--muted"
            description="Subtle backgrounds"
          />
          <ColorSwatch
            name="Muted Foreground"
            variable="--muted-foreground"
            description="Secondary text"
          />
          <ColorSwatch
            name="Accent"
            variable="--accent"
            description="Subtle emphasis"
          />
          <ColorSwatch
            name="Accent Foreground"
            variable="--accent-foreground"
            description="Text on accent background"
          />
        </div>
      </section>

      <Separator />

      {/* Base Colors */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Base Colors</h2>
          <p className="text-sm text-muted-foreground">
            Background, foreground, and structural colors
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          <ColorSwatch
            name="Background"
            variable="--background"
            description="Page background"
          />
          <ColorSwatch
            name="Foreground"
            variable="--foreground"
            description="Main text color"
          />
          <ColorSwatch
            name="Card"
            variable="--card"
            description="Card background"
          />
          <ColorSwatch
            name="Card Foreground"
            variable="--card-foreground"
            description="Text on cards"
          />
          <ColorSwatch
            name="Popover"
            variable="--popover"
            description="Popover background"
          />
          <ColorSwatch
            name="Popover Foreground"
            variable="--popover-foreground"
            description="Text in popovers"
          />
        </div>
      </section>

      <Separator />

      {/* Border & Input */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Border & Input Colors</h2>
          <p className="text-sm text-muted-foreground">
            Borders, dividers, and input elements
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          <ColorSwatch
            name="Border"
            variable="--border"
            description="Default border color"
          />
          <ColorSwatch
            name="Input"
            variable="--input"
            description="Input border color"
          />
          <ColorSwatch
            name="Ring"
            variable="--ring"
            description="Focus ring color"
          />
        </div>
      </section>

      <Separator />

      {/* Color Usage Examples */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Usage Examples</h2>
          <p className="text-sm text-muted-foreground">
            Color combinations in context
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="bg-primary text-primary-foreground">
              <CardTitle>Primary Card</CardTitle>
              <CardDescription className="text-primary-foreground/80">
                Using primary background color
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-sm">Content area with default card styling</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="bg-destructive text-destructive-foreground">
              <CardTitle>Destructive Card</CardTitle>
              <CardDescription className="text-destructive-foreground/80">
                Using destructive background color
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-sm">Content area with default card styling</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="bg-category-food text-white">
              <CardTitle>Food Category</CardTitle>
              <CardDescription className="text-white/80">
                Using food category color
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-sm">Content area with default card styling</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="bg-category-health text-white">
              <CardTitle>Health Category</CardTitle>
              <CardDescription className="text-white/80">
                Using health category color
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-sm">Content area with default card styling</p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
